import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  alpha,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import HistoryIcon from "@mui/icons-material/History";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: theme.shape.borderRadius * 2,
    minWidth: "900px",
    maxWidth: "1200px",
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: theme.palette.primary.contrastText,
  padding: theme.spacing(2, 3),
}));

const StatsCard = styled(Card)(({ theme }) => ({
  height: "100%",
  background: alpha(theme.palette.primary.light, 0.05),
  borderLeft: `4px solid ${theme.palette.primary.main}`,
}));

const PriceTypeChip = styled(Chip)(({ theme, pricetype }) => ({
  fontWeight: 600,
  ...(pricetype === "IMPORT_BILL" && {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.main,
  }),
  ...(pricetype === "SUB_ORDER" && {
    backgroundColor: alpha(theme.palette.secondary.main, 0.1),
    color: theme.palette.secondary.main,
  }),
  ...(pricetype === "MANUALLY_DEFINED" && {
    backgroundColor: alpha(theme.palette.info.main, 0.1),
    color: theme.palette.info.main,
  }),
}));

function PriceHistoryDialog({
  open,
  onClose,
  selectedItem,
  priceHistory,
  statistics,
}) {
  // Format data for chart
  const chartData =
    priceHistory
      ?.slice()
      .reverse()
      .map((item, index) => ({
        date: new Date(item.effectiveDate).toLocaleDateString(),
        price: item.price,
        index: index + 1,
      })) || [];

  // Get trend icon
  const getTrendIcon = (changeType) => {
    switch (changeType) {
      case "INCREASE":
        return <TrendingUpIcon color="error" fontSize="small" />;
      case "DECREASE":
        return <TrendingDownIcon color="success" fontSize="small" />;
      case "NO_CHANGE":
        return <TrendingFlatIcon color="info" fontSize="small" />;
      default:
        return null;
    }
  };

  // Format price type label
  const formatPriceType = (type) => {
    switch (type) {
      case "IMPORT_BILL":
        return "Import Bill";
      case "SUB_ORDER":
        return "Sub Order";
      case "MANUALLY_DEFINED":
        return "Manual";
      default:
        return type;
    }
  };

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <StyledDialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <HistoryIcon fontSize="large" />
            <Box>
              <Typography variant="h5" fontWeight="bold">
                Price History
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {selectedItem?.itemName} ({selectedItem?.productCode})
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </StyledDialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Statistics Section */}
        {statistics && (
          <>
            <Typography variant="h6" fontWeight="bold" mb={2}>
              Price Statistics (Last 6 Months)
            </Typography>
            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} sm={6} md={3}>
                <StatsCard>
                  <CardContent>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      gutterBottom
                    >
                      Current Price
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="primary">
                      Rs.{statistics.currentPrice?.toFixed(2)}
                    </Typography>
                  </CardContent>
                </StatsCard>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatsCard>
                  <CardContent>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      gutterBottom
                    >
                      Average Price
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="primary">
                      Rs.{statistics.avgPrice?.toFixed(2)}
                    </Typography>
                  </CardContent>
                </StatsCard>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatsCard>
                  <CardContent>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      gutterBottom
                    >
                      Highest Price
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="error">
                      Rs.{statistics.maxPrice?.toFixed(2)}
                    </Typography>
                  </CardContent>
                </StatsCard>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatsCard>
                  <CardContent>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      gutterBottom
                    >
                      Lowest Price
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="success">
                      Rs.{statistics.minPrice?.toFixed(2)}
                    </Typography>
                  </CardContent>
                </StatsCard>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <StatsCard>
                  <CardContent>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      gutterBottom
                    >
                      Total Changes
                    </Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {statistics.totalPriceChanges}
                    </Typography>
                  </CardContent>
                </StatsCard>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <StatsCard>
                  <CardContent>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      gutterBottom
                    >
                      Price Increases
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="error">
                      {statistics.priceIncreasesCount}
                    </Typography>
                  </CardContent>
                </StatsCard>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <StatsCard>
                  <CardContent>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      gutterBottom
                    >
                      Price Decreases
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="success">
                      {statistics.priceDecreasesCount}
                    </Typography>
                  </CardContent>
                </StatsCard>
              </Grid>
            </Grid>
          </>
        )}

        {/* Price Trend Chart */}
        {chartData.length > 0 && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" fontWeight="bold" mb={2}>
              Price Trend
            </Typography>
            <Box sx={{ width: "100%", height: 300, mb: 3 }}>
              <ResponsiveContainer>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip
                    formatter={(value) => `Rs.${value.toFixed(2)}`}
                    labelStyle={{ color: "#000" }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="price"
                    stroke="#1976d2"
                    strokeWidth={2}
                    dot={{ fill: "#1976d2", r: 5 }}
                    activeDot={{ r: 7 }}
                    name="Price"
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </>
        )}

        {/* Price History Table */}
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" fontWeight="bold" mb={2}>
          Price Change History
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: alpha("#000", 0.02) }}>
                <TableCell>
                  <Typography fontWeight="bold">Effective Date</Typography>
                </TableCell>
                <TableCell>
                  <Typography fontWeight="bold">Price</Typography>
                </TableCell>
                <TableCell>
                  <Typography fontWeight="bold">Change</Typography>
                </TableCell>
                <TableCell>
                  <Typography fontWeight="bold">Price Type</Typography>
                </TableCell>
                <TableCell>
                  <Typography fontWeight="bold">Method</Typography>
                </TableCell>
                <TableCell>
                  <Typography fontWeight="bold">Updated By</Typography>
                </TableCell>
                <TableCell>
                  <Typography fontWeight="bold">Status</Typography>
                </TableCell>
                <TableCell>
                  <Typography fontWeight="bold">Notes</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {priceHistory && priceHistory.length > 0 ? (
                priceHistory.map((item, index) => (
                  <TableRow
                    key={item.priceId}
                    sx={{
                      backgroundColor: item.isCurrent
                        ? alpha("#4caf50", 0.08)
                        : "inherit",
                      "&:hover": {
                        backgroundColor: alpha("#000", 0.04),
                      },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(item.effectiveDate).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {new Date(item.effectiveDate).toLocaleTimeString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight="bold" color="primary">
                        Rs.{item.price?.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {getTrendIcon(item.changeType)}
                        {item.previousPrice && (
                          <Typography
                            variant="caption"
                            color={
                              item.changeType === "INCREASE"
                                ? "error"
                                : item.changeType === "DECREASE"
                                ? "success"
                                : "textSecondary"
                            }
                          >
                            {item.changePercentage?.toFixed(1)}%
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <PriceTypeChip
                        label={formatPriceType(item.priceType)}
                        size="small"
                        pricetype={item.priceType}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {item.priceDefinedMethod || "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{item.updatedBy}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.isCurrent ? "Current" : item.status}
                        size="small"
                        color={item.isCurrent ? "success" : "default"}
                        variant={item.isCurrent ? "filled" : "outlined"}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="textSecondary">
                        {item.note || "-"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="textSecondary" py={2}>
                      No price history available
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions sx={{ p: 2, px: 3 }}>
        <Button onClick={onClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </StyledDialog>
  );
}

export default PriceHistoryDialog;
